/**
 * Strip capability-bearing secrets out of URLs before they reach analytics.
 *
 * Page addresses are sent to PostHog (and GTM) on every pageview, so any secret
 * carried in a path segment or a query parameter ends up stored in the
 * analytics project, where it is readable by anyone with access to it and long
 * outlives the page visit.
 *
 * The organizer invitation token (/invitations/accept?token=...) is the least
 * of the worry: it is single-use, expires in 7 days and is bound to the
 * invitee's email and user id, so a copy of it is useless to anyone else. The
 * ones that actually matter are the bearer links with none of those
 * protections:
 *
 *   /speaker-invite/<token>   one reusable link per organizer, no expiry
 *   /shared/stats/<token>     public stats share link
 *   /certificates/verify/<code>
 *
 * Redaction is deliberately structural (known prefixes and parameter names)
 * rather than pattern-matching anything that looks secret - a heuristic would
 * quietly stop matching the moment a token format changes.
 */

/** Query parameters whose value is a credential, whatever the path. */
const SENSITIVE_PARAMS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'otp',
  'code',
  'invitation_token',
]);

/** Parameters that carry another URL, which may itself embed a secret path. */
const NESTED_URL_PARAMS = new Set(['returnUrl', 'next', 'redirect', 'redirect_to']);

/** Path prefixes where the following segment is a bearer token. */
const SECRET_PATH_PREFIXES = [
  '/speaker-invite/',
  '/shared/stats/',
  '/certificates/verify/',
];

const PLACEHOLDER = '[redacted]';

/** Replace the secret segment in a path, leaving the shape intact. */
export function redactPath(pathname: string): string {
  for (const prefix of SECRET_PATH_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      const rest = pathname.slice(prefix.length);
      if (!rest) return pathname;
      const trailing = rest.slice(rest.indexOf('/') === -1 ? rest.length : rest.indexOf('/'));
      return `${prefix}${PLACEHOLDER}${trailing}`;
    }
  }
  return pathname;
}

/**
 * Redact a full or relative URL. Returns the input unchanged if it cannot be
 * parsed - analytics hygiene must never throw inside a capture path.
 */
export function redactUrl(url: string | undefined | null): string | undefined | null {
  if (!url) return url;

  try {
    // Relative URLs need a base to parse; it is discarded again below.
    const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : 'http://placeholder.invalid');

    parsed.pathname = redactPath(parsed.pathname);

    for (const key of Array.from(parsed.searchParams.keys())) {
      if (SENSITIVE_PARAMS.has(key)) {
        parsed.searchParams.set(key, PLACEHOLDER);
      } else if (NESTED_URL_PARAMS.has(key)) {
        const nested = parsed.searchParams.get(key);
        if (nested) parsed.searchParams.set(key, String(redactUrl(nested)));
      }
    }

    if (isAbsolute) return parsed.toString();
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

/** PostHog property keys that hold a URL or a path. */
const URL_PROPERTY_KEYS = [
  '$current_url',
  '$initial_current_url',
  '$pathname',
  '$initial_pathname',
  '$referrer',
  '$initial_referrer',
];

/** Redact every URL-bearing property on an outgoing analytics event. */
export function redactEventProperties<T extends Record<string, unknown>>(properties: T): T {
  for (const key of URL_PROPERTY_KEYS) {
    const value = properties[key];
    if (typeof value === 'string') {
      (properties as Record<string, unknown>)[key] = redactUrl(value);
    }
  }
  return properties;
}
